import { COLLECTION_TYPES } from '@/core/collections/constants';
import * as jsonpatch from 'fast-json-patch';
import { stampCharacterSummary } from '@mythictable/sheets';
import Character from '@/characters/models/model';
import { sendUpdate } from '@/core/collections/write';
import { toLibrarySlug } from '@/core/utils/libraryName';

// TODO: type this — Vuex module context
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

type CharacterRecord = Record<string, unknown>;

function findByOriginalId(characters: Record<string, CharacterRecord>, id: string): CharacterRecord | null {
    const matches = Object.values(characters).filter((character) => character._originalId === id);
    if (matches.length === 0) {
        return null;
    }
    return matches.sort((a, b) => String(a._id).localeCompare(String(b._id)))[0];
}

function resolveCharacter(rootGetters: Ctx, id: string): CharacterRecord | null {
    return (
        rootGetters['collections/getItem'](COLLECTION_TYPES.characters, id) ??
        findByOriginalId(rootGetters['collections/getCollection'](COLLECTION_TYPES.characters), id)
    );
}

export const getters = {
    getRawCharacter: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => (id: string) => {
        return resolveCharacter(rootGetters, id);
    },
    getCharacter: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => (id: string) => {
        return new Character(resolveCharacter(rootGetters, id));
    },
    getCharacters: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => {
        return Object.values(rootGetters['collections/getCollection'](COLLECTION_TYPES.characters)).map(
            (character) => new Character(character as Record<string, unknown>),
        );
    },
};

const PATH_FIELDS = ['path', '_path'];

const isSetString = (value: unknown): boolean => typeof value === 'string' && value.length > 0;

/** Saving a character must not move it in the library; only an explicit path may. */
const hasExplicitPath = (character: Ctx): boolean => PATH_FIELDS.some((field) => isSetString(character[field]));

const carryPathForward = (current: Ctx, updated: Ctx): void => {
    for (const field of PATH_FIELDS.filter((field) => current[field] !== undefined)) {
        updated[field] = current[field];
    }
};

const isPathOperation = (operation: Ctx): boolean => PATH_FIELDS.some((field) => operation.path === `/${field}`);

/** `_` fields are server-managed, and `name` is renamed through the library API. */
const isSendable = (operation: Ctx): boolean =>
    operation.path === '/_image' || (!operation.path.startsWith('/_') && operation.path !== '/name');

function buildPatch(current: Ctx, updated: Ctx, explicitPath: boolean): Ctx[] {
    return jsonpatch
        .compare(current, updated)
        .filter((operation) => explicitPath || !isPathOperation(operation))
        .filter(isSendable);
}

export const actions = {
    async add({ dispatch }: Ctx, character: unknown): Promise<unknown> {
        const item = { ...(character as Record<string, unknown>) };
        if (!item._name) {
            item._name = toLibrarySlug((item.name as string) || '', '.chr');
        }
        delete item.name;
        return await dispatch('collections/add', { collection: COLLECTION_TYPES.characters, item }, { root: true });
    },
    async remove({ dispatch }: Ctx, { id }: { id: string }): Promise<unknown> {
        return await dispatch('collections/remove', { collection: COLLECTION_TYPES.characters, id }, { root: true });
    },
    async update(context: Ctx, editedCharacter: Record<string, unknown>): Promise<unknown> {
        const current = context.getters['getRawCharacter'](editedCharacter._id as string);
        if (!current) throw new Error(`Could not find character to update with id '${editedCharacter._id}'`);

        const updated = new Character(editedCharacter);
        const explicitPath = hasExplicitPath(updated);
        if (!explicitPath) {
            carryPathForward(current, updated);
        }
        stampCharacterSummary(updated as unknown as Record<string, unknown>);

        const target = {
            collection: COLLECTION_TYPES.characters,
            id: editedCharacter._id as string,
            patch: buildPatch(current, updated, explicitPath),
        };

        // The sheet re-derives from the store in the same tick as the write, so waiting for the echo
        // makes a dragged bar render its old value and snap back. Committing first means a rejected
        // send cannot be rebuilt by re-diffing, so sendUpdate hands the patch back instead.
        context.commit('collections/patch', target, { root: true });

        return await sendUpdate(context.dispatch, target);
    },
    async load({ dispatch }: Ctx): Promise<unknown> {
        return await dispatch('collections/load', { collection: COLLECTION_TYPES.characters }, { root: true });
    },

    openEditor({ state }: Ctx, character: unknown): void {
        state.characterToEdit = character;
        state.editContextToken = null;
    },
    /** Open a character from one of its reference tokens; only the token's address is kept. */
    openEditorFromToken({ state }: Ctx, { character, token }: { character: unknown; token: Ctx }): void {
        state.characterToEdit = character;
        state.editContextToken = { id: token._id, mapId: token.mapId };
    },
    closeEditor({ state }: Ctx): void {
        state.characterToEdit = null;
        state.editContextToken = null;
    },

    openCreate({ state }: Ctx, context: { parentId: string } | null): void {
        state.newCharacter = context ?? {};
    },
    closeCreate({ state }: Ctx): void {
        state.newCharacter = null;
    },
};

const CharacterStore = {
    namespaced: true,
    state: {
        characterToEdit: null as unknown | null,
        editContextToken: null as { id: string; mapId: string } | null,
        newCharacter: null as { parentId?: string } | null,
    },
    getters,
    actions,
};

export default CharacterStore;
